import { createContext } from 'react';
import { usePurchaseOrdersDetails } from '../hooks/usePurchaseOrderDetails';
import { SelectChangeEvent } from '@mui/material';
import { Item, PurchaseOrderHeader } from '../../../app/services/api/generated';
import { LineRecord } from '../interfaces/LineRecord';
import { SelectableItem } from '../interfaces/SelectableItem';
import { Blocker } from 'react-router-dom';

export interface PurchaseOrderDetailsValues {
  downloadClicked: () => void,
  poLineChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  searchChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  handleNoteButtonClick: (id: number, note: string) => void,
  closeModal: () => void,
  closeStockCountModal: () => void,
  closeCreditRequestModal: () => void,
  createCreditRequestFromModal: () => void,
  closeCreditRequestCutOffModal: () => void,
  handleQuantityChange: (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => void,
  handleUomsChange: (id: number) => (event: SelectChangeEvent) => void,
  handleNotesChange: (id: number, note: string) => void,
  handleReasonChange: (id: number, reason: string) => void,
  sortedPoLines: LineRecord[] | undefined,
  isShowingTrafficCode: boolean | undefined,
  handlePurchaseOrderNoteChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  copyOrderQuantities: () => void,
  closeSnackBar: () => void,
  saveClicked: () => void,
  receiveClick: () => void,
  oneClickReceiveClick: () => void,
  returnToVendorClick: () => void,
  returnToVendorCancelClick: () => void,
  showReturnToVendor: boolean | undefined,
  isReturnToVendorPendingStatus: boolean | undefined,
  isReturnToVendorDateValid: boolean | undefined,
  isCreatingReturnToVendor: boolean | undefined,
  submitReturnToVendor: (rmaNumber: string) => void,
  rmaErrorMessage: string | undefined,
  setRmaErrorMessage: (errorMessage: string) => void,
  isReceiving: boolean,
  id: string | undefined,
  ewsOrderNumber : string | undefined,
  purchaseOrders: PurchaseOrderHeader | undefined,
  isStockCheckModalOpen: boolean,
  isCreditRequestModalOpen: boolean,
  isCreditRequestCutOffModalOpen: boolean,
  snackBarDuration: number | null,
  isSnackBarOpen: boolean,
  snackbarMessage: string,
  snackBarSeverity: 'error' | 'warning' | 'info' | 'success',
  note: string,
  noteId: number,
  purchaseOrderNote: string,
  search: string,
  poLineSearch: string,
  cutOffTime: string,
  isEdited: (line: LineRecord | undefined) => boolean,
  lines: LineRecord[],
  isNoteModalOpen: boolean,
  order: 'asc' | 'desc',
  orderBy: keyof LineRecord,
  handleSort: (orderBy: keyof LineRecord) => void,
  isConfirmOpen: boolean,
  closeConfirmModal: () => void,
  acceptStockCountModal: () => void,
  isLoading: boolean,
  receivePurchaseOrder: () => void,
  isProcessing: boolean,
  searchClick: () => void,
  clearAll: () => void,
  canAdjust: boolean,
  cannotOverReceive: boolean,
  showOverReceivingWarningModal: boolean,
  showCopyModal: boolean,
  showAdjustInstructionModal: boolean,
  showCopyQuantityModal: () => void,
  showAdjustButton: boolean,
  showReceive: boolean | undefined,
  adjustClicked: () => void,
  isEditable: boolean | undefined,
  confirmModal: () => void,
  isClickable: boolean,
  isAdjusting: boolean,
  cancelAdjust: () => void,
  showOneClickReceive: boolean | undefined,
  showSave: boolean,
  addItemClicked: () => void,
  showAddItemModal: boolean,
  handleSearchTermChange: (value: string) => void,
  items: Item[],
  isLoadingItemData: boolean,
  closeAddItemModal: () => void,
  itemSelected: (items: Item[]) => void,
  selectedItems: SelectableItem[],
  searchTerm: string,
  handleUomChange: (id: number, value: string) => void,
  handleModalQuantityChange: (id: number, value: string) => void,
  handleCostPriceChange: (id: number, value: string) => void,
  confirmAddItems: () => void,
  canStoreAddItemsToPO: boolean,
  showAutosaveMessage: boolean,
  handlePOLineFocus: (lineId: number) => void,
  handleLeavePageModalConfirm: () => void,
  handleLeavePageModalClose: () => void,
  handleLeavePageModalCancel: () => void,
  blocker: Blocker,
  modifiedPOLines?: Set<number>;
  isTaxRateConfigEnabled: boolean | undefined,
  totalCostPriceIncludingTax: number,
  closeOverReceivingWarning: () => void;
  disableUomLac: boolean;
  isDownloading: boolean;
}

export const PurchaseOrderDetailsContext = createContext<null | PurchaseOrderDetailsValues>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const PurchaseOrderDetailsProvider = ({ children }: ProviderProps) => {
  const {
    downloadClicked,
    poLineChange,
    searchChange,
    handleNoteButtonClick,
    closeModal,
    closeStockCountModal,
    closeCreditRequestModal,
    createCreditRequestFromModal,
    closeCreditRequestCutOffModal,
    handleQuantityChange,
    handleUomsChange,
    handleNotesChange,
    handleReasonChange,
    sortedPoLines,
    isShowingTrafficCode,
    handlePurchaseOrderNoteChange,
    copyOrderQuantities,
    closeSnackBar,
    saveClicked,
    receiveClick,
    oneClickReceiveClick,
    returnToVendorClick,
    returnToVendorCancelClick,
    showReturnToVendor,
    isReturnToVendorPendingStatus,
    isReturnToVendorDateValid,
    isCreatingReturnToVendor,
    submitReturnToVendor,
    rmaErrorMessage,
    setRmaErrorMessage,
    isReceiving,
    id,
    ewsOrderNumber,
    purchaseOrders,
    isStockCheckModalOpen,
    isCreditRequestModalOpen,
    isCreditRequestCutOffModalOpen,
    snackBarDuration,
    isSnackBarOpen,
    snackbarMessage,
    snackBarSeverity,
    note,
    noteId,
    purchaseOrderNote,
    search,
    poLineSearch,
    cutOffTime,
    isEdited,
    lines,
    isNoteModalOpen,
    order,
    orderBy,
    handleSort,
    isConfirmOpen,
    closeConfirmModal,
    acceptStockCountModal,
    isLoading,
    receivePurchaseOrder,
    isProcessing,
    searchClick,
    clearAll,
    canAdjust,
    cannotOverReceive,
    showOverReceivingWarningModal,
    showCopyModal,
    showAdjustInstructionModal,
    showCopyQuantityModal,
    showAdjustButton,
    showReceive,
    adjustClicked,
    isEditable,
    confirmModal,
    isClickable,
    isAdjusting,
    cancelAdjust,
    showOneClickReceive,
    showSave,
    addItemClicked,
    showAddItemModal,
    handleSearchTermChange,
    items,
    isLoadingItemData,
    closeAddItemModal,
    itemSelected,
    selectedItems,
    searchTerm,
    handleUomChange,
    handleModalQuantityChange,
    handleCostPriceChange,
    confirmAddItems,
    canStoreAddItemsToPO,
    showAutosaveMessage,
    handlePOLineFocus,
    handleLeavePageModalConfirm,
    handleLeavePageModalClose,
    handleLeavePageModalCancel,
    blocker,
    modifiedPOLines,
    isTaxRateConfigEnabled, 
    totalCostPriceIncludingTax,
    closeOverReceivingWarning,
    disableUomLac,
    isDownloading,
    transactionValidationDialog,
  } = usePurchaseOrdersDetails();

  const passedValue = {
    downloadClicked,
    poLineChange,
    searchChange,
    handleNoteButtonClick,
    closeModal,
    closeStockCountModal,
    closeCreditRequestModal,
    createCreditRequestFromModal,
    closeCreditRequestCutOffModal,
    handleQuantityChange,
    handleUomsChange,
    handleNotesChange,
    handleReasonChange,
    sortedPoLines,
    isShowingTrafficCode,
    handlePurchaseOrderNoteChange,
    copyOrderQuantities,
    closeSnackBar,
    saveClicked,
    receiveClick,
    oneClickReceiveClick,
    returnToVendorClick,
    returnToVendorCancelClick,
    showReturnToVendor,
    isReturnToVendorPendingStatus,
    isReturnToVendorDateValid,
    isCreatingReturnToVendor,
    submitReturnToVendor,
    rmaErrorMessage,
    setRmaErrorMessage,
    isReceiving,
    id,
    ewsOrderNumber,
    purchaseOrders,
    isStockCheckModalOpen,
    isCreditRequestModalOpen,
    isCreditRequestCutOffModalOpen,
    snackBarDuration,
    isSnackBarOpen,
    snackbarMessage,
    snackBarSeverity,
    note,
    noteId,
    purchaseOrderNote,
    search,
    poLineSearch,
    cutOffTime,
    isEdited,
    lines,
    isNoteModalOpen,
    order,
    orderBy,
    handleSort,
    isConfirmOpen,
    closeConfirmModal,
    acceptStockCountModal,
    isLoading,
    receivePurchaseOrder,
    isProcessing,
    searchClick,
    clearAll,
    canAdjust,
    cannotOverReceive,
    showOverReceivingWarningModal,
    showCopyModal,
    showAdjustInstructionModal,
    showCopyQuantityModal,
    showAdjustButton,
    showReceive,
    adjustClicked,
    isEditable,
    confirmModal,
    isClickable,
    isAdjusting,
    cancelAdjust,
    showOneClickReceive,
    showSave,
    addItemClicked,
    showAddItemModal,
    handleSearchTermChange,
    items,
    isLoadingItemData,
    closeAddItemModal,
    itemSelected,
    selectedItems,
    searchTerm,
    handleUomChange,
    handleModalQuantityChange,
    handleCostPriceChange,
    confirmAddItems,
    canStoreAddItemsToPO,
    showAutosaveMessage,
    handlePOLineFocus,
    handleLeavePageModalConfirm,
    handleLeavePageModalClose,
    handleLeavePageModalCancel,
    blocker,
    modifiedPOLines,
    isTaxRateConfigEnabled,
    totalCostPriceIncludingTax,
    closeOverReceivingWarning,
    disableUomLac,
    isDownloading,
  };

  return (
    <PurchaseOrderDetailsContext.Provider value={passedValue}>
      {children}
      {transactionValidationDialog}
    </PurchaseOrderDetailsContext.Provider>
  );
};